import { Button, Flex, Input, message, Popconfirm, Space, Table } from 'antd';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { TemplatePresetViewDTO, TemplateViewDTO } from '../../backendapi/requests';
import { useDebounce } from 'use-debounce';
import { Filter } from '../../types/types';
import { useTemplatePresetsServiceDeleteBackendv2Templatepreset, useTemplatePresetsServiceGetBackendv2Templatepreset, useTemplatesServiceDeleteBackendv2Template, useTemplatesServiceGetBackendv2Template } from '../../backendapi/queries';
import { GenerateODataFilter } from '../../helpers/odataFunctions';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { ConfigContext } from '../../contexts/ConfigContext';
import CreateTemplatePreset from './CreateTemplatePreset';
import EditTemplatePreset from './EditTemplatePreset';


const TemplatePresetList: React.FC = () => {

     const { currentClient } = useContext(ConfigContext);
    
    const [messageApi, contextHolder] = message.useMessage();
    const [selectedPreset, setSelectedPreset] = useState<TemplatePresetViewDTO | undefined>(undefined);

    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebounce(query, 500);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

    const filters = useMemo(() => {
        let filters: Filter[] = [];

        if (debouncedQuery) {
            filters.push({type: 'search', values: [{value: debouncedQuery}], property: 'title', name: 'title'});
        }

        return filters;
    }, [debouncedQuery]);

    const { data, isLoading, refetch } = useTemplatePresetsServiceGetBackendv2Templatepreset({         
        clientId: currentClient.id,
        top: pageSize.toString(), 
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true' })
    const {mutateAsync, isSuccess} = useTemplatePresetsServiceDeleteBackendv2Templatepreset();


    const openCreate = () => {
        setCreateOpen(true);
    };

    const handleEdit = useCallback((template: TemplatePresetViewDTO) => {
        setSelectedPreset(template);
        setEditOpen(true);
    }, []);

    const handleCreateClose = useCallback(() => {
        setCreateOpen(false);
        refetch();
    }, [refetch]);

    const handleEditClose = useCallback(() => {
        setEditOpen(false);
        refetch();
    }, [refetch]);

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<TemplatePresetViewDTO> | SorterResult<TemplatePresetViewDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if(!Array.isArray(sorter)){
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const handleDelete = useCallback((preset: TemplatePresetViewDTO) => {
        if(preset.id){
            mutateAsync({key: preset.id}).then(refetch);
        }
    }, [mutateAsync]);

    useEffect(() => {
        if(isSuccess){
            messageApi.success("Preset deleted");
        }
    }, [isSuccess, messageApi]);

    const columns: ColumnsType<TemplatePresetViewDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => handleEdit(record)}>Edit</Button>
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                        <Button type="link">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}

            <Flex justify='space-between' style={{ marginBottom: 16 }}>
                <Space>
                    <Input prefix={<SearchOutlined />} placeholder='Search' value={query} onChange={e => setQuery(e.target.value)} />
                </Space>

                <Space>
                    <Button type="primary" onClick={openCreate} icon={<PlusOutlined />}>
                        New preset
                    </Button>
                </Space>
            </Flex>

            <CreateTemplatePreset isOpen={createOpen} onClose={handleCreateClose} config={undefined} templateId={undefined} />

            <EditTemplatePreset isOpen={editOpen} onClose={handleEditClose} preset={selectedPreset} />

            <Table
                loading={isLoading}
                dataSource={data?.value}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                showSorterTooltip={false}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                }} />
        </>
    );
};

export default TemplatePresetList;